import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";
import type { z } from "zod";

import { ProtectedInformation } from "@/assets/onboarding/protected-information";
import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackNamePageViewed } from "@/features/analytics/trackers/name-page-viewed";
import { onboardingUserSchema } from "@/features/onboarding/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { StateField } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import { User } from "@/shared/domains/users";
import { Form, Input, SectionHeader, UserIcon } from "@/shared/ui";
import { QuestionNavigation, QuestionStep } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

const schema = User.omit({ email: true }).extend({
  state: onboardingUserSchema.shape.state,
});

export const PersonalDetails = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();

  const personalDetails = useOnboardingStore((state) => state.personalDetails);
  const setPersonalDetails = useOnboardingStore(
    (state) => state.setPersonalDetails,
  );

  const intakeFormId = useQuestionnaireStore((state) => state.intakeFormId);
  const storedState = useQuestionnaireStore((state) => state.state);
  const setState = useQuestionnaireStore((state) => state.setState);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      firstName: personalDetails?.firstName,
      lastName: personalDetails?.lastName,
      state: storedState,
    },
  });

  useEffect(() => {
    trackNamePageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!intakeFormId) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.INTAKE} replace />;
  }

  const handleNext = form.handleSubmit(({ state, ...names }) => {
    setPersonalDetails(names);
    setState(state);
    navigate(QUESTIONNAIRE_ROUTE.CONTACT_DETAILS);
  });

  return (
    <Form.Provider {...form}>
      <QuestionStep
        onSubmit={handleNext}
        footer={
          <QuestionNavigation
            onBack={() => navigate(QUESTIONNAIRE_ROUTE.INTAKE)}
          />
        }
      >
        <SectionHeader title={t`Tell us about yourself`} />

        <div className="flex w-full flex-col gap-4 sm:max-w-xl">
          <Form.Field
            control={form.control}
            name="firstName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`First name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("firstName")}
                    placeholder={t`E.g. John`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name="lastName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Last name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("lastName")}
                    placeholder={t`E.g. Doe`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <StateField
            className="mf-masked mf-listen"
            contentClassName="mf-masked mf-listen"
            control={form.control}
            categorySlug={CATEGORY_SLUG.WEIGHT_LOSS}
          />

          <ProtectedInformation />
        </div>
      </QuestionStep>
    </Form.Provider>
  );
};
